import React, { useState, useEffect, useMemo } from "react";
import api, { configurePortalBackend } from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider, StylesProvider, jssPreset } from "@material-ui/core/styles";
import { createTheme as createModernTheme, ThemeProvider as ModernThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@material-ui/core";
import { StylesProvider as ModernStylesProvider } from "@mui/styles";
import { create } from "jss";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/dominium-light.svg";
import defaultLogoDark from "./assets/dominium-dark.svg";
import defaultLogoFavicon from "./assets/dominium-mark.svg";
import useSettings from "./hooks/useSettings";

import "./styles/animations.css";
import "./styles/corporate.css";
import { corporateTheme, resolvePrimaryColor, DEFAULT_APP_NAME } from "./design/theme";
import { nativeDensityPlugin, nativeDensityMiddleware } from "./design/density";

const queryClient = new QueryClient();
const nativeJss = create({ plugins: [...jssPreset().plugins, nativeDensityPlugin()] });
const nativeEmotionCache = createCache({
  key: "mui",
  stylisPlugins: [nativeDensityMiddleware, prefixer],
});

const App = () => {
  const locale = ptBR;
  const appNameLocalStorage = localStorage.getItem("appName") || DEFAULT_APP_NAME;
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme === "dark" ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(resolvePrimaryColor(localStorage.getItem("primaryColorLight")));
  const [primaryColorDark, setPrimaryColorDark] = useState(resolvePrimaryColor(localStorage.getItem("primaryColorDark"), "dark"));
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  const [portal, setPortal] = useState(null);
  const [portalError, setPortalError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadPortal = () => api.get("/whitelabel/portal").then(({ data }) => {
      if (!mounted) return;
      configurePortalBackend(data.scope);
      if (data.scope === "reseller") {
        const initials = (data.name || "Portal").replace(/[^a-z0-9]/gi, "").slice(0, 2).toUpperCase();
        const mark = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="120" height="60" viewBox="0 0 120 60"><rect width="120" height="60" rx="8" fill="${data.primaryColorLight}"/><text x="60" y="39" text-anchor="middle" fill="white" font-family="sans-serif" font-size="28">${initials}</text></svg>`)}`;
        const asset = (value) => value ? value.startsWith("/") ? getBackendUrl() + value : value : mark;
        setAppName(data.name);
        setPrimaryColorLight(data.primaryColorLight);
        setPrimaryColorDark(data.primaryColorDark);
        setAppLogoLight(asset(data.logoLight));
        setAppLogoDark(asset(data.logoDark || data.logoLight));
        setAppLogoFavicon(asset(data.favicon || data.logoLight));
      }
      setPortal(data);
    }).catch(() => { if (mounted) setPortalError("Não foi possível carregar este portal. Confira o endereço ou tente novamente."); });
    loadPortal();
    window.addEventListener("whitelabel:branding", loadPortal);
    return () => { mounted = false; window.removeEventListener("whitelabel:branding", loadPortal); };
  }, []);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          ...corporateTheme(mode, portal?.scope === "reseller" ? (mode === "light" ? primaryColorLight : primaryColorDark) : resolvePrimaryColor(mode === "light" ? primaryColorLight : primaryColorDark, mode)),
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
      portal?.scope,
    ]
  );

  const modernTheme = useMemo(() => {
    const tokens = corporateTheme(mode, portal?.scope === "reseller" ? (mode === "light" ? primaryColorLight : primaryColorDark) : resolvePrimaryColor(mode === "light" ? primaryColorLight : primaryColorDark, mode));
    return createModernTheme({
      palette: tokens.palette,
      breakpoints: tokens.breakpoints,
      typography: tokens.typography,
      shape: tokens.shape,
      components: Object.fromEntries(Object.entries(tokens.overrides).map(([name, styles]) => [name, { styleOverrides: styles }])),
    });
  }, [mode, primaryColorLight, primaryColorDark, portal?.scope]);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    if (portal?.scope !== "central") return;
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(resolvePrimaryColor(color));
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(resolvePrimaryColor(color, "dark"));
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || DEFAULT_APP_NAME);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName(DEFAULT_APP_NAME);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal?.scope]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    document.title = appName || DEFAULT_APP_NAME;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode, appName]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <CacheProvider value={nativeEmotionCache}>
          <StylesProvider jss={nativeJss}>
            <ModernThemeProvider theme={modernTheme}>
              <ModernStylesProvider jss={nativeJss}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <QueryClientProvider client={queryClient}>
                    <ActiveMenuProvider>
                      {portalError ? <div role="alert" style={{ padding: "3rem", textAlign: "center" }}>{portalError}</div> : !portal ?
                        <div role="status" style={{ padding: "3rem", textAlign: "center" }}>Carregando portal…</div> : portal.blocked ?
                        <div style={{ padding: "3rem", textAlign: "center" }}><h1>{portal.name}</h1><p role="alert">Acesso suspenso. Entre em contato com seu administrador.</p></div> : <Routes />}
                    </ActiveMenuProvider>
                  </QueryClientProvider>
                </ThemeProvider>
              </ModernStylesProvider>
            </ModernThemeProvider>
          </StylesProvider>
        </CacheProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
